/**
 * LoanPerformance — Full-tab NOT_CONNECTED (LOS)
 *
 * All loan performance metrics require a Loan Origination System integration.
 * This tab renders a comprehensive not-connected state with the required
 * data fields listed for the lender's integration team.
 */
import React from 'react';
import { FiLock, FiDatabase } from 'react-icons/fi';
import { NOT_CONNECTED_REGISTRY } from '../../../types/insightsTypes';

const REQUIRED_FIELDS = [
  { field: 'application_id', description: 'Unique loan application identifier' },
  { field: 'application_status', description: 'Current status (submitted, approved, funded, denied)' },
  { field: 'submitted_at', description: 'Application submission timestamp' },
  { field: 'approved_at', description: 'Decision/approval timestamp' },
  { field: 'funded_at', description: 'Loan funding timestamp' },
  { field: 'loan_type', description: 'Loan product type (auto, personal, home, credit card)' },
  { field: 'loan_amount', description: 'Requested or funded loan amount' },
  { field: 'delinquency_status', description: 'Current delinquency/default status' },
];

const LoanPerformance: React.FC = () => {
  const losInfo = NOT_CONNECTED_REGISTRY.los;

  return (
    <div className="space-y-6">
      {/* Header */}
      <h3 className="text-base sm:text-lg font-medium text-slate-700">
        Loan performance metrics and insights
      </h3>

      {/* Full-tab NOT_CONNECTED card */}
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 sm:p-10 flex flex-col items-center justify-center text-center">
        <div className="bg-slate-200 p-4 rounded-full mb-4">
          <FiLock className="text-slate-500" size={28} />
        </div>
        <h4 className="text-lg font-semibold text-slate-700 mb-2">{losInfo.label}</h4>
        <p className="text-sm text-slate-500 max-w-md mb-6">{losInfo.description}</p>

        {/* Metrics that will be available once LOS is connected */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8 w-full max-w-2xl">
          {['Conversion Rate', 'Applications', 'Avg Decision Time', 'Default Rate'].map((name) => (
            <div key={name} className="bg-white rounded-lg border border-slate-200 p-3 text-center">
              <p className="text-xs text-slate-400 mb-1">{name}</p>
              <p className="text-lg font-bold text-slate-300">—</p>
            </div>
          ))}
        </div>

        {/* Required data fields */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 w-full max-w-2xl text-left">
          <div className="flex items-center gap-2 mb-4">
            <FiDatabase className="text-slate-500" size={16} />
            <h5 className="text-sm font-semibold text-slate-700">Required Data Fields</h5>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {REQUIRED_FIELDS.map((f) => (
              <div key={f.field} className="flex items-start gap-2">
                <code className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono flex-shrink-0">
                  {f.field}
                </code>
                <span className="text-xs text-slate-500">{f.description}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanPerformance;